import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  BusinessOutlined as BusinessOutlinedIcon,
  CalendarTodayOutlined as CalendarIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  DragIndicator as DragIndicatorIcon,
  DrawOutlined as DrawOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  HomeOutlined as HomeOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  MonetizationOnOutlined as MonetizationOnOutlinedIcon,
  NumbersOutlined as NumbersOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  StarOutline as StarOutlineIcon,
  Tune as TuneIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
} from "@mui/icons-material";
import { FavouriteDragItem } from "./FavouriteDragAndDrop";

const FAVOURITES_STORAGE_KEY = "pdf-editor-field-favourites-v1";

const FAVOURITE_CATEGORIES = [
  { key: "project", label: "Project", color: "#64748b" },
  { key: "owner", label: "Owner", color: "#2563eb" },
  { key: "contractor", label: "Contractor", color: "#d97706" },
  { key: "ownerSignature", label: "Owner signature", color: "#2563eb" },
  {
    key: "contractorSignature",
    label: "Contractor signature",
    color: "#d97706",
  },
  {
    key: "ownerNotarization",
    label: "Owner notarization",
    color: "#2563eb",
  },
  {
    key: "contractorNotarization",
    label: "Contractor notarization",
    color: "#d97706",
  },
  { key: "other", label: "Other", color: "#64748b" },
];

const FAVOURITE_ICON_OPTIONS = {
  description: {
    label: "Document",
    icon: <DescriptionOutlinedIcon fontSize="small" />,
  },
  number: { label: "Number", icon: <NumbersOutlinedIcon fontSize="small" /> },
  location: {
    label: "Location",
    icon: <LocationOnOutlinedIcon fontSize="small" />,
  },
  person: { label: "Person", icon: <PersonOutlineIcon fontSize="small" /> },
  business: {
    label: "Business",
    icon: <BusinessOutlinedIcon fontSize="small" />,
  },
  home: { label: "Property", icon: <HomeOutlinedIcon fontSize="small" /> },
  calendar: { label: "Date", icon: <CalendarIcon fontSize="small" /> },
  tag: { label: "Tag", icon: <LocalOfferOutlinedIcon fontSize="small" /> },
  money: {
    label: "Money",
    icon: <MonetizationOnOutlinedIcon fontSize="small" />,
  },
  phone: { label: "Phone", icon: <PhoneOutlinedIcon fontSize="small" /> },
  email: { label: "Email", icon: <EmailOutlinedIcon fontSize="small" /> },
  badge: {
    label: "ID / License",
    icon: <BadgeOutlinedIcon fontSize="small" />,
  },
  key: { label: "Key", icon: <KeyOutlinedIcon fontSize="small" /> },
  signature: {
    label: "Signature",
    icon: <DrawOutlinedIcon fontSize="small" />,
  },
  seal: {
    label: "Notary seal",
    icon: <VerifiedOutlinedIcon fontSize="small" />,
  },
};

const DEFAULT_FAVOURITE_TEMPLATES = [
  { category: "project", terms: ["permit number"], icon: "number" },
  { category: "project", terms: ["project address"], icon: "location" },
  {
    category: "project",
    terms: ["parcel_id", "Tax Folio/Parcel ID"],
    icon: "tag",
  },
  { category: "owner", terms: ["owner name"], icon: "person" },
  { category: "owner", terms: ["owner phone"], icon: "phone" },
  { category: "owner", terms: ["owner email"], icon: "email" },
  { category: "owner", terms: ["owner_full_address"], icon: "home" },
  {
    category: "contractor",
    terms: ["contractor_company_name"],
    icon: "business",
  },
  { category: "contractor", terms: ["contractor_full_name"], icon: "person" },

  { category: "contractor", terms: ["contractor phone"], icon: "phone" },
  { category: "contractor", terms: ["contractor_full_address"], icon: "home" },
  { category: "contractor", terms: ["contractor email"], icon: "email" },
  { category: "ownerSignature", terms: ["owner signature"], icon: "signature" },
  {
    category: "ownerSignature",
    terms: ["Owner Signature Full Address"],
    icon: "home",
  },

  {
    category: "contractorSignature",
    terms: ["contractor signature"],
    icon: "signature",
  },
  {
    category: "contractorSignature",
    terms: ["contractor_sign_address"],
    icon: "home",
  },
  {
    category: "ownerNotarization",
    terms: ["owner notary seal", "owner notarization seal"],
    icon: "seal",
  },
  {
    category: "ownerNotarization",
    terms: ["owner notary signature"],
    icon: "signature",
  },
  {
    category: "contractorNotarization",
    terms: ["contractor notary seal", "contractor notarization seal"],
    icon: "seal",
  },
  {
    category: "contractorNotarization",
    terms: ["contractor notary signature"],
    icon: "signature",
  },
];

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/[’']/g, "'")
    .replace(/'s\b/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getFieldId = (field) =>
  field?.field_id ?? field?.id ?? field?.field_name ?? field?.name;

const getFieldLabel = (field) =>
  field?.field_label ??
  field?.label ??
  field?.field_name ??
  field?.name ??
  "Field";

const flattenFavouriteFields = (fields = []) => {
  const result = [];
  const seenIds = new Set();

  const visit = (items) => {
    if (!Array.isArray(items)) return;

    items.forEach((field) => {
      if (!field || typeof field !== "object") return;

      const fieldId = getFieldId(field);

      if (fieldId !== undefined && fieldId !== null && fieldId !== "") {
        const key = String(fieldId);

        if (!seenIds.has(key)) {
          seenIds.add(key);
          result.push(field);
        }
      }

      visit(field.children);
    });
  };

  visit(fields);
  return result;
};

const createDefaultFavourites = (fields) => {
  const usedFieldIds = new Set();

  return DEFAULT_FAVOURITE_TEMPLATES.reduce((favourites, template, index) => {
    const matchingFields = fields.filter((field) => {
      const fieldId = getFieldId(field);
      return fieldId && !usedFieldIds.has(fieldId);
    });

    const terms = template.terms.map(normalizeText);

    // Prefer an exact label/name match.
    const matchedField =
      matchingFields.find((field) =>
        [getFieldLabel(field), field?.field_name]
          .filter(Boolean)
          .some((label) => terms.includes(normalizeText(label))),
      ) ||
      matchingFields.find((field) => {
        const searchableText = normalizeText(
          `${getFieldLabel(field)} ${field?.field_name ?? ""} ${
            field?.category_label ?? ""
          }`,
        );

        return terms.some((term) => searchableText.includes(term));
      });

    if (!matchedField) return favourites;

    const fieldId = getFieldId(matchedField);
    usedFieldIds.add(fieldId);
    favourites.push({
      id: `default-${template.category}-${fieldId}-${index}`,
      fieldId,
      category: template.category,
      label: getFieldLabel(matchedField),
      icon: template.icon,
    });
    return favourites;
  }, []);
};

const getAutomaticCategories = (signatures = {}) => {
  const categories = [];

  ["owner", "contractor"].forEach((person) => {
    const selection = signatures?.[person];

    if (selection?.sign === true) {
      categories.push(`${person}Signature`);
    }

    if (selection?.notary === true) {
      categories.push(`${person}Notarization`);
    }
  });

  return categories;
};

const readStoredFavourites = () => {
  try {
    if (typeof window === "undefined") return null;

    const raw = window.localStorage.getItem(FAVOURITES_STORAGE_KEY);
    if (raw === null) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const seenIds = new Set();

    return parsed.filter((item) => {
      if (
        !item ||
        typeof item.id !== "string" ||
        !["string", "number"].includes(typeof item.fieldId) ||
        typeof item.label !== "string" ||
        !FAVOURITE_CATEGORIES.some(
          (category) => category.key === item.category,
        ) ||
        seenIds.has(item.id)
      ) {
        return false;
      }

      seenIds.add(item.id);
      return true;
    });
  } catch {
    return null;
  }
};

const FavouritesBar = ({
  fields = [],
  onFavouriteSelect,
  signerContext,
  loading = false,
}) => {
  const availableFields = useMemo(
    () => flattenFavouriteFields(fields),
    [fields],
  );
  const [expanded, setExpanded] = useState(true);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedFavouriteId, setDraggedFavouriteId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(["project", "owner", "contractor", "other"]),
  );
  const [draftFavourite, setDraftFavourite] = useState({
    fieldId: "",
    category: "project",
    label: "",
    icon: "description",
  });
  const [storedFavourites] = useState(readStoredFavourites);

  const [favourites, setFavourites] = useState(() => storedFavourites ?? []);

  const initializedRef = useRef(storedFavourites !== null);
  const [preferencesReady, setPreferencesReady] = useState(
    storedFavourites !== null,
  );

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteInputRef = useRef(null);

  const automaticCategories = useMemo(
    () => getAutomaticCategories(signerContext),
    [signerContext],
  );

  const automaticCategoryKey = automaticCategories.join("|");

  const previousAutomaticCategoriesRef = useRef(new Set());

  useEffect(() => {
    if (initializedRef.current || availableFields.length === 0) return;

    initializedRef.current = true;
    setFavourites(createDefaultFavourites(availableFields));
    setPreferencesReady(true);
  }, [availableFields]);

  useEffect(() => {
    if (!preferencesReady || typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        FAVOURITES_STORAGE_KEY,
        JSON.stringify(favourites),
      );
    } catch {
      // Keep favourites usable for this session.
    }
  }, [favourites, preferencesReady]);

  useEffect(() => {
    const activeCategories = new Set(
      automaticCategoryKey ? automaticCategoryKey.split("|") : [],
    );

    const previousCategories = previousAutomaticCategoriesRef.current;

    const newlyActivated = [...activeCategories].filter(
      (category) => !previousCategories.has(category),
    );

    const deactivated = [...previousCategories].filter(
      (category) => !activeCategories.has(category),
    );

    if (newlyActivated.length > 0) {
      setExpanded(true);
    }

    if (newlyActivated.length > 0 || deactivated.length > 0) {
      setExpandedCategories((previous) => {
        const next = new Set(previous);

        deactivated.forEach((category) => next.delete(category));
        newlyActivated.forEach((category) => next.add(category));

        return next;
      });
    }

    previousAutomaticCategoriesRef.current = activeCategories;
  }, [automaticCategoryKey]);

  const categoryMap = useMemo(
    () =>
      FAVOURITE_CATEGORIES.reduce((result, category) => {
        result[category.key] = favourites.filter(
          (favourite) => favourite.category === category.key,
        );
        return result;
      }, {}),
    [favourites],
  );

  const resolveField = (fieldId) =>
    availableFields.find(
      (field) => String(getFieldId(field)) === String(fieldId),
    );

  const handleDraftFieldChange = (fieldId) => {
    const field = resolveField(fieldId);
    setDraftFavourite((previous) => ({
      ...previous,
      fieldId,
      label: field ? getFieldLabel(field) : previous.label,
    }));
  };

  const handleAddFavourite = () => {
    const field = resolveField(draftFavourite.fieldId);
    if (!field) return;

    setFavourites((previous) => {
      const alreadyExists = previous.some(
        (item) =>
          String(item.fieldId) === String(draftFavourite.fieldId) &&
          item.category === draftFavourite.category,
      );

      if (alreadyExists) return previous;

      return [
        ...previous,
        {
          ...draftFavourite,
          id: `favourite-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`,
          label: draftFavourite.label.trim() || getFieldLabel(field),
        },
      ];
    });

    setExpanded(true);
    setExpandedCategories((previous) =>
      new Set(previous).add(draftFavourite.category),
    );

    setDraftFavourite((previous) => ({
      ...previous,
      fieldId: "",
      label: "",
    }));
  };

  const handleFavouriteChange = (id, key, value) => {
    setFavourites((previous) =>
      previous.map((favourite) =>
        favourite.id === id ? { ...favourite, [key]: value } : favourite,
      ),
    );
  };

  const handleRemoveFavourite = (id) => {
    setFavourites((previous) =>
      previous.filter((favourite) => favourite.id !== id),
    );
  };

  const handleDrop = (targetFavourite, sourceId = draggedFavouriteId) => {
    if (loading || !sourceId || sourceId === targetFavourite.id) return;

    setFavourites((previous) => {
      const sourceIndex = previous.findIndex((item) => item.id === sourceId);

      const targetIndex = previous.findIndex(
        (item) => item.id === targetFavourite.id,
      );

      if (sourceIndex < 0 || targetIndex < 0) return previous;

      if (previous[sourceIndex].category !== previous[targetIndex].category) {
        return previous;
      }

      const next = [...previous];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);

      return next;
    });

    setDraggedFavouriteId(null);
  };

  const handleSelectFavourite = (favourite) => {
    const field = resolveField(favourite.fieldId);
    if (field) onFavouriteSelect?.(field, favourite);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(categoryKey)) next.delete(categoryKey);
      else next.add(categoryKey);
      return next;
    });
  };

  const populatedCategories = FAVOURITE_CATEGORIES.filter(
    (category) =>
      categoryMap[category.key]?.length > 0 ||
      automaticCategories.includes(category.key),
  );

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        elevation={0}
        sx={{
          borderTop: "1px solid #e0e0e0",
          borderRadius: "0 !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minHeight: 42,
            px: 2,
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              my: 0.75,
            },
          }}
        >
          <StarOutlineIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography variant="body2" fontWeight={700}>
            Favourites
          </Typography>
          <Chip label={favourites.length} size="small" sx={{ height: 20 }} />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
            onClick={(event) => {
              event.stopPropagation();
              setCustomizeOpen(true);
            }}
            onFocus={(event) => event.stopPropagation()}
            sx={{ textTransform: "none", py: 0.25 }}
          >
            Customize
          </Button>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
          {populatedCategories.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Add commonly used form fields to create your shortcuts.
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCustomizeOpen(true)}
                sx={{ mt: 0.5, textTransform: "none" }}
              >
                Add favourite
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(225px, 1fr))",
                gap: 1,
              }}
            >
              {populatedCategories.map((category) => {
                const categoryFavourites = categoryMap[category.key];
                const categoryExpanded = expandedCategories.has(category.key);
                const isAutomatic = automaticCategories.includes(category.key);

                return (
                  <Accordion
                    key={category.key}
                    expanded={categoryExpanded}
                    onChange={() => toggleCategory(category.key)}
                    disableGutters
                    elevation={0}
                    sx={{
                      alignSelf: "start",
                      border: "1px solid #dbe3ec",
                      borderLeft: `3px solid ${category.color}`,
                      borderRadius: "8px !important",
                      overflow: "hidden",
                      boxShadow: isAutomatic
                        ? `0 0 0 2px ${category.color}22`
                        : "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        minHeight: 34,
                        px: 1,
                        "&.Mui-expanded": { minHeight: 34 },
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 0.5,
                          my: 0.5,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          my: 0.5,
                        },
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {category.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {categoryFavourites.length}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                      {categoryFavourites.length === 0 && (
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            No favourites added to this category yet.
                          </Typography>

                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setDraftFavourite((previous) => ({
                                ...previous,
                                category: category.key,
                              }));
                              setCustomizeOpen(true);
                            }}
                            sx={{
                              display: "flex",
                              mt: 0.5,
                              textTransform: "none",
                            }}
                          >
                            Add favourite
                          </Button>
                        </Box>
                      )}
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                      >
                        {categoryFavourites.map((favourite) => (
                          <FavouriteDragItem
                            key={favourite.id}
                            favourite={favourite}
                            label={
                              resolveField(favourite.fieldId)
                                ? getFieldLabel(resolveField(favourite.fieldId))
                                : favourite.label
                            }
                            disabled={
                              loading || !resolveField(favourite.fieldId)
                            }
                            onReorder={handleDrop}
                          >
                            <Tooltip
                              title={
                                resolveField(favourite.fieldId)
                                  ? getFieldLabel(
                                      resolveField(favourite.fieldId),
                                    )
                                  : `${favourite.label} — field unavailable`
                              }
                              arrow
                              placement="top"
                              enterDelay={250}
                              disableInteractive
                            >
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  size="small"
                                  disabled={
                                    loading || !resolveField(favourite.fieldId)
                                  }
                                  aria-label={
                                    resolveField(favourite.fieldId)
                                      ? getFieldLabel(
                                          resolveField(favourite.fieldId),
                                        )
                                      : favourite.label
                                  }
                                  onClick={() =>
                                    handleSelectFavourite(favourite)
                                  }
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    border: "1px solid #d0d7de",
                                    borderRadius: 1.5,
                                    color: category.color,
                                    cursor: loading ? "default" : "grab",
                                    "&:hover": {
                                      backgroundColor: `${category.color}12`,
                                      borderColor: category.color,
                                    },
                                    "&:active": {
                                      cursor: "grabbing",
                                    },
                                  }}
                                >
                                  {FAVOURITE_ICON_OPTIONS[favourite.icon]
                                    ?.icon ??
                                    FAVOURITE_ICON_OPTIONS.description.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </FavouriteDragItem>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StarOutlineIcon color="warning" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Customize favourites
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add shortcuts, choose their icon and category, or drag icons in
              the favourites bar to reorder them.
            </Typography>
          </Box>
          <IconButton onClick={() => setCustomizeOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          onScroll={() => {
            if (autocompleteOpen) {
              setAutocompleteOpen(false);
              autocompleteInputRef.current?.blur();
            }
          }}
          sx={{ overflow: "auto" }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Add a favourite
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(180px, 1.4fr) minmax(150px, 1fr) minmax(160px, 1fr) auto",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              open={autocompleteOpen}
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              options={availableFields}
              value={resolveField(draftFavourite.fieldId) ?? null}
              getOptionLabel={(field) => getFieldLabel(field)}
              isOptionEqualToValue={(option, value) =>
                String(getFieldId(option)) === String(getFieldId(value))
              }
              onChange={(_, field) =>
                handleDraftFieldChange(field ? getFieldId(field) : "")
              }
              renderOption={(props, field) => (
                <li {...props} key={String(getFieldId(field))}>
                  {getFieldLabel(field)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={autocompleteInputRef}
                  label="Form field"
                  placeholder="Search fields"
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={draftFavourite.category}
                label="Category"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    category: event.target.value,
                  }))
                }
              >
                {FAVOURITE_CATEGORIES.map((category) => (
                  <MenuItem key={category.key} value={category.key}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={draftFavourite.icon}
                label="Icon"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    icon: event.target.value,
                  }))
                }
              >
                {Object.entries(FAVOURITE_ICON_OPTIONS).map(([key, option]) => (
                  <MenuItem key={key} value={key}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {option.icon}
                      <span>{option.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!draftFavourite.fieldId}
              onClick={handleAddFavourite}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Add
            </Button>
          </Box>
          <TextField
            value={draftFavourite.label}
            label="Custom tooltip label (optional)"
            size="small"
            fullWidth
            onChange={(event) =>
              setDraftFavourite((previous) => ({
                ...previous,
                label: event.target.value,
              }))
            }
            sx={{ mt: 1 }}
          />

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Current favourites
          </Typography>
          {favourites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No favourites have been added yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {favourites.map((favourite) => (
                <Box
                  key={favourite.id}
                  onDragOver={(event) => {
                    if (draggedFavouriteId) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleDrop(favourite);
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "auto 1fr auto",
                      sm: "auto minmax(160px, 1.5fr) minmax(150px, 1fr) minmax(145px, 1fr) auto",
                    },
                    gap: 1,
                    alignItems: "center",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Box
                    component="span"
                    draggable
                    title="Drag to reorder within this category"
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", favourite.id);
                      setDraggedFavouriteId(favourite.id);
                    }}
                    onDragEnd={() => setDraggedFavouriteId(null)}
                    sx={{ display: "inline-flex", cursor: "grab" }}
                  >
                    <DragIndicatorIcon color="action" />
                  </Box>
                  <TextField
                    value={favourite.label}
                    label="Tooltip label"
                    size="small"
                    onChange={(event) =>
                      handleFavouriteChange(
                        favourite.id,
                        "label",
                        event.target.value,
                      )
                    }
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={favourite.category}
                      label="Category"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "category",
                          event.target.value,
                        )
                      }
                    >
                      {FAVOURITE_CATEGORIES.map((category) => (
                        <MenuItem key={category.key} value={category.key}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Icon</InputLabel>
                    <Select
                      value={favourite.icon}
                      label="Icon"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "icon",
                          event.target.value,
                        )
                      }
                    >
                      {Object.entries(FAVOURITE_ICON_OPTIONS).map(
                        ([key, option]) => (
                          <MenuItem key={key} value={key}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {option.icon}
                              <span>{option.label}</span>
                            </Stack>
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <Tooltip title="Remove favourite">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveFavourite(favourite.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCustomizeOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FavouritesBar;
